/**
 * Notification Item Component
 * REQ-SOP-6.2: Individual notification card
 */

import React from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import { Notification } from '../services/notificationAPI';
import { useNavigate } from 'react-router-dom';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  onDelete: (id: number) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onDelete
}) => {
  const navigate = useNavigate();

  // Get icon based on notification type
  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  // Format time ago
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    // PostgreSQL returns timestamp without timezone info, so treat it as UTC
    const date = new Date(dateString.includes('Z') ? dateString : dateString + 'Z');
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    // Handle negative values (clock skew)
    if (diffInSeconds < 0) return 'Just now';

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  // Handle click on notification
  const handleClick = () => {
    // Mark as read
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }

    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
    }
  };

  // Handle delete click
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent notification click
    onDelete(notification.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`
        p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer
        ${!notification.is_read ? 'bg-blue-50' : 'bg-white'}
      `}
    >
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {getIcon()}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-gray-900 ${!notification.is_read ? 'font-semibold' : ''}`}>
            {notification.title}
          </p>
          {notification.message && (
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
              {notification.message}
            </p>
          )}
          <p className="text-xs text-gray-500 mt-1">
            {getTimeAgo(notification.created_at)}
          </p>
        </div>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          title="Delete notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500"></div>
      )}
    </div>
  );
};

export default NotificationItem;
